// <copyright file="DleRecordTypeSchema.tsx">
// © 2024 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Toast } from "primereact/toast";
import React from "react";
import styles from "src/components/DataLakeExplorer/DleRecordTypeSchema.module.css";
import type { FC, useState as _useState } from "react";
import type {
    SchemaVersion,
    SchemaVersionField,
} from "src/components/DataLakeExplorer/DleRecordType";

// Properties to initialize this component
interface DleRecordTypeSchemaProps {
    useState?: typeof _useState; // optional hook override (for tests)
    recordType: string;
    schemaVersion: SchemaVersion | undefined;
    tabularSchema: SchemaVersionField[];
}

// Info/error message popup
const toast = React.createRef<Toast>();

/**
 * Determines whether to display the content view for this component
 * @param props Component properties
 * @returns True if view should display, else false
 */
function shouldDisplayView(props: DleRecordTypeSchemaProps) {
    return props.tabularSchema.length > 0;
}

/**
 * DLE record type schema component
 * @param props Component props
 * @returns The component
 */
const DleRecordTypeSchema: FC<DleRecordTypeSchemaProps> = (
    props: DleRecordTypeSchemaProps
) => {
    const columns = [
        { field: "FieldIndex", header: "Index" },
        { field: "FieldName", header: "Name" },
        { field: "FieldType", header: "Type" },
        { field: "FieldDoc", header: "Description" },
    ];

    return (
        <div
            className={styles.DleRecordTypeSchema}
            data-testid="DleRecordTypeSchema"
        >
            <Toast ref={toast} />
            {(shouldDisplayView(props) && (
                <DataTable value={props.tabularSchema} showGridlines>
                    {columns.map((col) => (
                        <Column
                            key={col.field}
                            field={col.field}
                            header={col.header}
                        />
                    ))}
                </DataTable>
            )) || <div>Schema version detail content was not loaded.</div>}
        </div>
    );
};

export default DleRecordTypeSchema;
